/**
 * QZ Tray — integração para impressão silenciosa em impressoras térmicas.
 *
 * Requer QZ Tray instalado no computador local (qz.io/download).
 * O script qz-tray.js deve estar carregado via CDN no index.html.
 *
 * Para HTTPS (app.oplyx.com.br): QZ Tray precisa de certificado assinado.
 * Configure REACT_APP_QZ_CERTIFICATE com o conteúdo do certificado público,
 * e implemente assinatura no backend ou use QZ Tray em modo não-seguro
 * (somente para redes privadas/internas). Veja /settings/printers/guide.
 *
 * Fallback: window.print() é sempre usado quando QZ Tray não está disponível.
 */

import { api } from "@/lib/api";

// ─── Estado de conexão ────────────────────────────────────────────────────────

let _connectPromise = null;

function qz() {
  return window.qz;
}

// ─── Conexão ──────────────────────────────────────────────────────────────────

export async function connectQZ() {
  if (!qz()) throw new Error("QZ Tray não carregado. Verifique o CDN no index.html");
  if (qz().websocket.isActive()) return;
  if (_connectPromise) return _connectPromise;

  // Certificado: em produção configure REACT_APP_QZ_CERTIFICATE no .env
  qz().security.setCertificatePromise((_resolve) => {
    const cert = process.env.REACT_APP_QZ_CERTIFICATE || "";
    _resolve(cert);
  });
  qz().security.setSignatureAlgorithm("SHA512");
  qz().security.setSignaturePromise(() => (_resolve) => {
    // Para assinar em produção: chame um endpoint do backend que assine com a chave privada.
    // Deixar vazio só funciona quando QZ Tray está em modo não-seguro (redes internas).
    _resolve("");
  });

  _connectPromise = qz().websocket
    .connect({ host: "localhost", port: 8181, usingSecure: true })
    .catch(() => qz().websocket.connect({ host: "localhost", port: 8182, usingSecure: false }))
    .then(() => { _connectPromise = null; })
    .catch((err) => { _connectPromise = null; throw err; });

  return _connectPromise;
}

export async function isQZAvailable() {
  if (!qz()) return false;
  try {
    if (qz().websocket.isActive()) return true;
    await connectQZ();
    return true;
  } catch {
    return false;
  }
}

export async function disconnectQZ() {
  if (qz()?.websocket.isActive()) await qz().websocket.disconnect();
}

export async function findPrinters() {
  await connectQZ();
  return qz().printers.find();
}

// ─── Cache de impressoras da filial ──────────────────────────────────────────

const CACHE_TTL = 60_000;
let _cache = { data: null, ts: 0 };

export function invalidatePrinterCache() {
  _cache = { data: null, ts: 0 };
}

export async function getBranchPrinter(destination) {
  const now = Date.now();
  if (!_cache.data || now - _cache.ts > CACHE_TTL) {
    try {
      const { data } = await api.get("/printers");
      _cache = { data, ts: now };
    } catch {
      return null;
    }
  }
  const printers = _cache.data || [];
  return (
    printers.find((p) => p.destination === destination && p.active && p.is_default) ||
    printers.find((p) => p.destination === destination && p.active) ||
    null
  );
}

// ─── Construtores ESC/POS ─────────────────────────────────────────────────────

const ESC = "\x1B";
const GS  = "\x1D";
const LF  = "\x0A";

const CMD = {
  INIT:         ESC + "@",
  BOLD_ON:      ESC + "E\x01",
  BOLD_OFF:     ESC + "E\x00",
  ALIGN_LEFT:   ESC + "a\x00",
  ALIGN_CENTER: ESC + "a\x01",
  SIZE_NORMAL:  GS  + "!\x00",
  SIZE_2H:      GS  + "!\x01",
  SIZE_2W2H:    GS  + "!\x11",
  CUT_PARTIAL:  GS  + "V\x01",
};

function sep(w) { return "-".repeat(w) + LF; }
function cols(left, right, w) {
  const maxL = w - right.length - 1;
  return left.slice(0, maxL).padEnd(maxL) + " " + right + LF;
}
function brl(v) {
  return Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// ─── Impressão raw ────────────────────────────────────────────────────────────

export async function printESCPOS(printerConfig, lines) {
  await connectQZ();
  const name = printerConfig.name;
  const config = qz().configs.create(name);
  await qz().print(config, [{ type: "raw", format: "plain", data: lines.join("") }]);
}

// ─── Ticket de comanda ────────────────────────────────────────────────────────

export async function printTicket(job, printerConfig) {
  const w = printerConfig.paper_width === 57 ? 32 : 42;
  const DEST = {
    cozinha: "COZINHA", bar: "BAR", atendimento: "ATENDIMENTO", caixa: "CAIXA",
  };
  const destLabel = DEST[job.destination] || job.destination.toUpperCase();

  const lines = [
    CMD.INIT,
    CMD.ALIGN_CENTER, CMD.SIZE_2W2H, CMD.BOLD_ON, destLabel + LF, CMD.BOLD_OFF, CMD.SIZE_NORMAL,
    sep(w), CMD.ALIGN_LEFT,
    CMD.BOLD_ON, `COMANDA ${job.tab_label}` + LF, CMD.BOLD_OFF,
    new Date(job.created_at).toLocaleString("pt-BR") + LF,
    job.created_by_name ? `Garcom: ${job.created_by_name}` + LF : "",
    sep(w),
  ];

  for (const it of job.items) {
    lines.push(CMD.BOLD_ON, `${it.quantity}x ${it.name}` + LF, CMD.BOLD_OFF);
    for (const m of (it.modifiers || [])) {
      lines.push(`  >> ${m.group}: ${m.option}` + LF);
    }
    if (it.notes) lines.push(`  OBS: ${it.notes}` + LF);
  }

  lines.push(sep(w), CMD.ALIGN_CENTER, "-- OPLYX --" + LF, LF, LF, CMD.CUT_PARTIAL);
  await printESCPOS(printerConfig, lines);
}

// ─── Cupom de venda ───────────────────────────────────────────────────────────

export async function printReceipt(sale, biz, printerConfig) {
  const w = printerConfig.paper_width === 57 ? 32 : 42;
  const lines = [
    CMD.INIT, CMD.ALIGN_CENTER, CMD.BOLD_ON, CMD.SIZE_2H,
    (biz?.business_name || "OPLYX") + LF,
    CMD.SIZE_NORMAL, CMD.BOLD_OFF,
    "Cupom nao fiscal" + LF,
    new Date(sale.date).toLocaleString("pt-BR") + LF,
    `#${sale.id.slice(0, 8).toUpperCase()}` + LF,
    sep(w), CMD.ALIGN_LEFT,
  ];

  if (sale.customer) {
    lines.push(`Cliente: ${sale.customer.name}` + LF);
    if (sale.customer.phone) lines.push(sale.customer.phone + LF);
    lines.push(sep(w));
  }

  for (const it of sale.items) {
    lines.push(cols(`${it.quantity}x ${it.name}`, brl(it.subtotal), w));
    lines.push(`  @ ${brl(it.price)}` + LF);
  }

  lines.push(
    sep(w),
    cols("Subtotal", brl(sale.total_amount), w),
    ...(sale.cashback_used > 0 ? [cols("Cashback", `-${brl(sale.cashback_used)}`, w)] : []),
    ...(sale.tip_amount > 0    ? [cols("Gorjeta",  `+${brl(sale.tip_amount)}`,   w)] : []),
    CMD.BOLD_ON,
    cols("TOTAL", brl((sale.net_amount || 0) + (sale.tip_amount || 0)), w),
    CMD.BOLD_OFF, sep(w),
    `Pagamento: ${(sale.payment_method || "").toUpperCase()}` + LF,
  );
  if (sale.employee_name) lines.push(`Atendente: ${sale.employee_name}` + LF);
  if (sale.nfce_access_key) lines.push(sep(w), `NFC-e: ${sale.nfce_access_key}` + LF);
  if (sale.cashback_generated > 0) {
    lines.push(CMD.ALIGN_CENTER, `Cashback ganho: ${brl(sale.cashback_generated)}` + LF);
  }
  lines.push(CMD.ALIGN_CENTER, sep(w), "Obrigado pela preferencia!" + LF, "-- OPLYX --" + LF, LF, LF, CMD.CUT_PARTIAL);
  await printESCPOS(printerConfig, lines);
}

// ─── Conferência de consumo (pré-conta) ──────────────────────────────────────
// As linhas vêm prontas do backend (conferencia_lib.py) — aqui só se desenha
// na largura do papel. Mesmo desenho do Agente de Impressão.

export async function printLinhas(linhas, printerConfig) {
  const w = printerConfig.paper_width === 57 ? 32 : 42;
  const SIZE = { small: CMD.SIZE_NORMAL, medium: CMD.SIZE_2H, big: CMD.SIZE_2W2H };
  const ALIGN = { left: CMD.ALIGN_LEFT, center: CMD.ALIGN_CENTER, right: ESC + "a\x02" };
  const lines = [CMD.INIT];
  for (const ln of linhas || []) {
    if (ln.t === "sep") {
      lines.push(CMD.ALIGN_LEFT, sep(w));
    } else if (ln.t === "cols") {
      lines.push(CMD.ALIGN_LEFT);
      if (ln.bold) lines.push(CMD.BOLD_ON, cols(ln.left, ln.right, w), CMD.BOLD_OFF);
      else lines.push(cols(ln.left, ln.right, w));
    } else if (ln.t === "text") {
      const limit = ln.size === "big" ? Math.floor(w / 2) : w;
      lines.push(
        ALIGN[ln.align] || CMD.ALIGN_LEFT, SIZE[ln.size] || CMD.SIZE_NORMAL,
        ln.bold ? CMD.BOLD_ON : "", String(ln.text || "").slice(0, limit) + LF,
        CMD.BOLD_OFF, CMD.SIZE_NORMAL,
      );
    }
  }
  lines.push(CMD.ALIGN_LEFT, LF, LF, LF, CMD.CUT_PARTIAL);
  await printESCPOS(printerConfig, lines);
}

// ─── Fechamento de caixa ──────────────────────────────────────────────────────

export async function printCashierClose(report, printerConfig) {
  const w = printerConfig.paper_width === 57 ? 32 : 42;
  const diff = (report.actual_close || 0) - (report.expected_close || 0);
  const diffStr = (diff >= 0 ? "+" : "") + brl(diff);

  const lines = [
    CMD.INIT, CMD.ALIGN_CENTER, CMD.BOLD_ON, "FECHAMENTO DE CAIXA" + LF, CMD.BOLD_OFF,
    new Date().toLocaleString("pt-BR") + LF,
    sep(w), CMD.ALIGN_LEFT,
    cols("Abertura",       brl(report.opening_amount), w),
    cols("Vendas dinheiro",brl(report.cash_sales),     w),
    cols("Vendas cartao",  brl(report.card_sales),     w),
    cols("Vendas Pix",     brl(report.pix_sales),      w),
    sep(w),
    CMD.BOLD_ON, cols("Total em caixa",  brl(report.expected_close), w), CMD.BOLD_OFF,
    cols("Contagem fisica", brl(report.actual_close),  w),
    sep(w),
    CMD.BOLD_ON, cols("Diferenca", diffStr, w), CMD.BOLD_OFF,
  ];
  if (report.justification) lines.push(`Justificativa: ${report.justification}` + LF);
  lines.push(LF, CMD.ALIGN_CENTER, "-- OPLYX --" + LF, LF, LF, CMD.CUT_PARTIAL);
  await printESCPOS(printerConfig, lines);
}
